import { CHAT_UPLOAD_MIME_TYPES } from '@wundo/shared/attachments.ts';
import type { ChatSendResponse } from '@wundo/shared/chat.ts';
import type {
  MeetingCompletedActionItem,
  MeetingCorrectionRequest,
  MeetingOpenedDocument,
  MeetingOpenedRequest,
  MeetingPageDocument,
  MeetingPageOfflineRecording,
  MeetingPagePrep,
  MeetingPageResponse,
  MeetingPrepContent,
  MeetingReportSection,
  MeetingTemplateScope,
  MeetingTranscriptResponse,
  MeetingTranscriptSegment,
} from '@wundo/shared/meetingPage.ts';
import type { MeetingTopicJudgmentResponse } from '@wundo/shared/meetingTopicJudgment.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import { reportError } from './reportError.ts';

export type {
  MeetingCompletedActionItem,
  MeetingCorrectionRequest,
  MeetingPageDocument,
  MeetingPageOfflineRecording,
  MeetingPagePrep,
  MeetingPageResponse,
  MeetingPrepContent,
  MeetingReportSection,
  MeetingTemplateScope,
  MeetingTranscriptResponse,
  MeetingTranscriptSegment,
};

/**
 * What may become a document of a conversation: the files Wundo takes anywhere,
 * without the pictures. An image is something you send along with a message,
 * never something the preparation is written from — so the picker here offers
 * the same list the chat does, minus those.
 */
export const THREAD_DOCUMENT_MIME_TYPES: string[] = CHAT_UPLOAD_MIME_TYPES.filter(
  (mime) => !mime.startsWith('image/'),
);

/**
 * Everything one meeting's page shows above its tabs.
 *
 * Addressed by user id like the rest of the calendar: the same route serves a
 * superadmin looking at someone else's meeting, and the policy decides who may,
 * not the path.
 */
export async function fetchMeetingPage(
  userId: string,
  meetingId: string,
): Promise<MeetingPageResponse> {
  return apiFetchJson<MeetingPageResponse>(`/users/${userId}/meetings/${meetingId}/page`);
}

/**
 * The raw transcript of a meeting, turn by turn.
 *
 * Not part of the page: the transcript is never shown to the people a meeting
 * belongs to, and only our own superadmins may read one — for support, when a
 * verslag does not match what was said. The server answers 404 both to everyone
 * else and to a meeting that has no transcript, so the two are indistinguishable
 * from here, which is the point.
 */
export async function fetchMeetingTranscript(
  userId: string,
  meetingId: string,
): Promise<MeetingTranscriptResponse> {
  return apiFetchJson<MeetingTranscriptResponse>(
    `/users/${userId}/meetings/${meetingId}/transcript`,
  );
}

/** Superadmin-only, like the transcript: anyone else gets the 404 of a missing meeting. */
export async function fetchMeetingTopicJudgment(
  userId: string,
  meetingId: string,
): Promise<MeetingTopicJudgmentResponse> {
  return apiFetchJson<MeetingTopicJudgmentResponse>(
    `/users/${userId}/meetings/${meetingId}/topic-judgment`,
  );
}

const action = (userId: string, meetingId: string, name: string) =>
  `/users/${userId}/meetings/${meetingId}/actions/${name}`;

const recording = (userId: string, meetingId: string, name: string) =>
  `/users/${userId}/meetings/${meetingId}/recording/${name}`;

/**
 * Begin recording this meeting through the microphone of the device the user is
 * sitting at.
 *
 * Idempotent while one is already running for this user: it answers with the
 * state as it stands, which is what makes it the resume path too — a page that
 * was reloaded mid-recording asks for the same thing and gets back the index to
 * carry on counting from.
 */
export async function startOfflineRecording(
  userId: string,
  meetingId: string,
): Promise<MeetingPageOfflineRecording> {
  return apiFetchJson<MeetingPageOfflineRecording>(
    recording(userId, meetingId, 'start'),
    { method: 'POST' },
  );
}

/**
 * Hand over half a minute of audio. Multipart, field `file`, with the index in
 * the query: the server stores every piece under its own number, so the order
 * they arrive in is not the order they are read back in.
 *
 * The content type is left to the browser — a multipart body's own type carries
 * the boundary that separates its parts, and only the browser knows what it
 * generated.
 */
export async function uploadOfflineRecordingChunk(
  userId: string,
  meetingId: string,
  index: number,
  chunk: Blob,
): Promise<void> {
  const body = new FormData();
  body.append('file', chunk, `chunk-${index}.webm`);
  await apiFetch(`${recording(userId, meetingId, 'chunks')}?index=${index}`, {
    method: 'POST',
    body,
  });
}

/**
 * Say the recording is over. Everything recorded has to be uploaded first — the
 * server starts reading the moment it is told to.
 */
export async function stopOfflineRecording(
  userId: string,
  meetingId: string,
): Promise<void> {
  await apiFetch(recording(userId, meetingId, 'stop'), { method: 'POST' });
}

/**
 * Change what Wundo may do with this meeting. Only what moved is sent — the
 * server leaves out what it is not told about, so correcting the notetaker
 * never touches a standing choice about following.
 */
export async function correctMeeting(
  userId: string,
  meetingId: string,
  correction: MeetingCorrectionRequest,
): Promise<void> {
  await apiFetch(action(userId, meetingId, 'correct'), {
    method: 'POST',
    body: JSON.stringify(correction),
  });
}

/**
 * Say the page showed the user one of the meeting's documents. The server
 * counts it once a day; a lost count is not worth disturbing the page over.
 */
export async function reportMeetingDocumentOpened(
  userId: string,
  meetingId: string,
  document: MeetingOpenedDocument,
): Promise<void> {
  const body: MeetingOpenedRequest = { document };
  try {
    await apiFetch(action(userId, meetingId, 'opened'), {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (caught) {
    reportError(caught, { action: 'reportMeetingDocumentOpened', document });
  }
}

/**
 * Write the preparation. Without `regenerate` the server does nothing once
 * Wundo has written one, which is what lets the page fire this on first open
 * without knowing whether it already did.
 *
 * The preparation comes back, so the page can put it on screen itself. Asking
 * for the whole page again instead would spend the seconds this took a second
 * time, with the meeting already on screen and nothing else about it changed.
 */
export async function generateMeetingPrep(
  userId: string,
  meetingId: string,
  regenerate = false,
): Promise<MeetingPagePrep> {
  return apiFetchJson<MeetingPagePrep>(action(userId, meetingId, 'generate-prep'), {
    method: 'POST',
    body: JSON.stringify({ regenerate }),
  });
}

/**
 * Answer the intake opener. This is the first thing said about a meeting Wundo
 * has nothing to prepare from, and it is what creates the conversation: the
 * server puts its own opener in front of this message, so the drawer sends only
 * what the user brought — words, files, or both. The answer is a chat send's
 * answer — the same conversation id, the same message back — and the reply
 * arrives over the socket like any other. Everything said after it goes the
 * ordinary way.
 */
export async function sendMeetingIntake(
  userId: string,
  meetingId: string,
  message: string,
  attachmentIds: string[],
): Promise<ChatSendResponse> {
  return apiFetchJson<ChatSendResponse>(action(userId, meetingId, 'intake'), {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
    }),
  });
}

/**
 * Answer the debrief opener. The same door as the intake, one conversation
 * later: this is the first thing said about a meeting that is over and that
 * Wundo has nothing to write the verslag from, and it is what creates the
 * conversation the verslag comes out of. The server puts its own opener in
 * front of this message, so the drawer sends only what the user brought —
 * words, files, or both — and the answer is a chat send's answer like any
 * other.
 */
export async function sendMeetingDebrief(
  userId: string,
  meetingId: string,
  message: string,
  attachmentIds: string[],
): Promise<ChatSendResponse> {
  return apiFetchJson<ChatSendResponse>(action(userId, meetingId, 'debrief'), {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
    }),
  });
}

/**
 * Say the first thing about the summary of the previous conversation. This is
 * what creates the conversation the card was standing in front of: the server
 * writes its own summary ahead of this message, so the drawer sends only what
 * the user brought — words, files, or both. The answer is a chat send's
 * answer — the same conversation id, the same message back — and everything
 * said after it goes the ordinary way.
 */
export async function sendMeetingRecap(
  userId: string,
  meetingId: string,
  message: string,
  attachmentIds: string[],
): Promise<ChatSendResponse> {
  return apiFetchJson<ChatSendResponse>(action(userId, meetingId, 'recap'), {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
    }),
  });
}

/** Save the whole document. The page owns it while it is being edited. */
export async function saveMeetingPrep(
  userId: string,
  meetingId: string,
  content: MeetingPrepContent,
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/prep`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
}

/**
 * Pick another gesprekstype, with or without writing the topics again, for this
 * one meeting or for the whole conversation. The scope is only sent when the
 * page had reason to ask for it; the server takes this one meeting otherwise.
 */
export async function setMeetingTemplate(
  userId: string,
  meetingId: string,
  templateId: string,
  recreate: boolean,
  scope?: MeetingTemplateScope,
): Promise<void> {
  await apiFetch(action(userId, meetingId, 'set-template'), {
    method: 'POST',
    body: JSON.stringify({ template_id: templateId, recreate, scope }),
  });
}

export async function addMeetingActionItem(
  userId: string,
  meetingId: string,
  text: string,
): Promise<void> {
  await apiFetch(action(userId, meetingId, 'action-items'), {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

/** Reword an agreement, tick it off, or both — whatever is passed. */
export async function updateMeetingActionItem(
  userId: string,
  meetingId: string,
  itemId: string,
  changes: { text?: string; done?: boolean },
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/action-items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}

/**
 * Everything ever ticked off on this thread, newest first.
 *
 * Read on the thread rather than on the meeting, because that is where
 * agreements live — and only when the user asks for it, so an archive that
 * grows for years never weighs on opening a page.
 */
export async function fetchCompletedActionItems(
  userId: string,
  threadId: string,
): Promise<MeetingCompletedActionItem[]> {
  const { data } = await apiFetchJson<{ data: MeetingCompletedActionItem[] }>(
    `/users/${userId}/meeting-threads/${threadId}/completed-action-items`,
  );
  return data;
}

export async function deleteMeetingActionItem(
  userId: string,
  meetingId: string,
  itemId: string,
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/action-items/${itemId}`, {
    method: 'DELETE',
  });
}

/**
 * Keep your notes for a meeting. There is one note per user and meeting, so
 * saving again replaces what was there.
 */
export async function saveMeetingNote(
  userId: string,
  meetingId: string,
  content: string,
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/materials`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

/** The note's full text — the page itself only carries an excerpt. */
export async function fetchMeetingNote(
  userId: string,
  meetingId: string,
  materialId: string,
): Promise<string> {
  const { content } = await apiFetchJson<{ content: string }>(
    `/users/${userId}/meetings/${meetingId}/materials/${materialId}`,
  );
  return content;
}

export async function deleteMeetingMaterial(
  userId: string,
  meetingId: string,
  materialId: string,
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/materials/${materialId}`, {
    method: 'DELETE',
  });
}

/**
 * Put a file on this conversation, for Wundo to read along at every meeting in
 * it. Multipart, field `file`, content type left to the browser so the boundary
 * is right. What comes back is the document as the page lists it — still being
 * read, because reading it happens after the answer.
 *
 * Addressed on the thread rather than on the meeting: a document belongs to the
 * conversation, and the page it was added from is only where somebody clicked.
 */
export async function uploadThreadDocument(
  userId: string,
  threadId: string,
  file: File,
): Promise<MeetingPageDocument> {
  const body = new FormData();
  body.append('file', file);
  return apiFetchJson<MeetingPageDocument>(
    `/users/${userId}/meeting-threads/${threadId}/documents`,
    { method: 'POST', body },
  );
}

/** Take a document off the conversation, bytes and all. */
export async function deleteThreadDocument(
  userId: string,
  threadId: string,
  documentId: string,
): Promise<void> {
  await apiFetch(`/users/${userId}/meeting-threads/${threadId}/documents/${documentId}`, {
    method: 'DELETE',
  });
}

/**
 * Write the report from everything the meeting has. Without `regenerate` the
 * server does nothing once there is one — replacing it is a decision the user
 * makes behind a warning, never a side effect of opening the page.
 */
export async function generateMeetingReport(
  userId: string,
  meetingId: string,
  regenerate = false,
): Promise<void> {
  await apiFetch(action(userId, meetingId, 'generate-report'), {
    method: 'POST',
    body: JSON.stringify({ regenerate }),
  });
}

/** Save the whole document. The page owns it while it is being edited. */
export async function saveMeetingReport(
  userId: string,
  meetingId: string,
  sections: MeetingReportSection[],
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}/report`, {
    method: 'PUT',
    body: JSON.stringify({ sections }),
  });
}
